// src/components/ItemCheckoutPage.js - Enhanced version with Header and Footer
import React, { useState, useEffect } from "react";
import {
  Container,
  Paper,
  Typography,
  Button,
  Box,
  Card,
  CardContent,
  CardMedia,
  Divider,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Chip,
  IconButton,
  Grid,
  Rating,
  AppBar,
  Toolbar,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Collapse,
  Skeleton,
} from "@mui/material";
import {
  ArrowBack,
  Payment as PaymentIcon,
  AccountBalance as UPIIcon,
  ShoppingBag,
  CheckCircle,
  ContentCopy,
  LocalShipping,
  VerifiedUser,
  LocationOn,
  Add,
} from "@mui/icons-material";
import ItemPurchaseHandler from "../services/ItemPurchaseHandler";
import Footer from "./Footer";
import { AddressService } from "../services/AddressService";
import { calculateDeliveryFee, getDeliveryMessage } from "../utils/validation";
import { brandPalette as primaryBlue } from "../config/brandTheme";
import { BANK_DETAILS } from "../config/paymentConfig";

const ItemCheckoutPage = ({
  itemData,
  userProfile,
  isAdmin,
  onBack,
  onPurchaseComplete,
  user,
}) => {
  const [paymentConfirmDialog, setPaymentConfirmDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const [addresses, setAddresses] = useState([]);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [addressLoading, setAddressLoading] = useState(true);

  const upiId = "infinitywhale@sbi";

  // Calculate amounts
  const itemPrice = itemData.sellingPrice;
  const basePrice = itemData.basePrice || itemData.sellingPrice; // Use basePrice for GST calculation
  const quantity = itemData.quantity || 1;
  const subtotal = itemPrice * quantity;
  const baseSubtotal = basePrice * quantity; // Base price subtotal for GST

  // Calculate GST on BASE PRICE (not selling price)
  const rawGst = itemData.gstPercentage;
  const gstPercentage =
    rawGst !== undefined && rawGst !== null && rawGst !== ""
      ? Number(rawGst)
      : 18;
  const gstAmount = (baseSubtotal * gstPercentage) / 100;
  const subtotalWithGst = subtotal + gstAmount;
  const deliveryFee = calculateDeliveryFee(subtotalWithGst, user?.email);
  const totalAmount = subtotalWithGst + deliveryFee;
  const deliveryMessage = getDeliveryMessage(subtotalWithGst, user?.email);

  // Calculate money saved
  const originalPrice = itemData.compareAtPrice || 0;
  const originalTotal = originalPrice * quantity;
  const moneySaved = originalTotal - totalAmount;
  const hasSavings = originalPrice > itemPrice;

  // Get selected options
  const selectedSize = itemData.selectedSize || "";
  const selectedColor = itemData.selectedColor || "";

  const formatPrice = (price) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
    }).format(price);
  };

  // Fetch user addresses
  useEffect(() => {
    const fetchAddresses = async () => {
      try {
        setAddressLoading(true);
        const result = await AddressService.getUserAddresses(user.uid);

        if (result.success) {
          setAddresses(result.data);
          // Auto-select primary address if available
          const primaryAddress = result.data.find((addr) => addr.isPrimary);
          if (primaryAddress) {
            setSelectedAddress(primaryAddress.id);
          }
        } else {
          console.error("Failed to fetch addresses:", result.error);
        }
      } catch (error) {
        console.error("Error fetching addresses:", error);
      } finally {
        setAddressLoading(false);
      }
    };

    fetchAddresses();
  }, [user.uid]);

  const handleAddressChange = (event) => {
    setSelectedAddress(event.target.value);
  };

  const handlePayment = () => {
    // Validate that an address is selected if addresses exist
    if (addresses.length > 0 && !selectedAddress) {
      alert("Please select a delivery address before proceeding with payment.");
      return;
    }
    setPaymentConfirmDialog(true);
  };

  const handlePaymentConfirm = async () => {
    // Validate that an address is selected
    if (!selectedAddress && addresses.length > 0) {
      alert("Please select a delivery address before proceeding with payment.");
      return;
    }

    setLoading(true);

    try {
      // Get the selected address data
      const selectedAddressData = addresses.find(
        (addr) => addr.id === selectedAddress
      );

      const result = await ItemPurchaseHandler.processItemPurchase({
        buyerId: user.uid,
        buyerName: userProfile.name,
        buyerEmail: user.email,
        item: {
          ...itemData,
          quantity: quantity,
          selectedSize: selectedSize,
          selectedColor: selectedColor,
        },
        userProfile: userProfile,
        deliveryAddress: selectedAddressData,
      });

      setTimeout(() => {
        setLoading(false);
        setPaymentConfirmDialog(false);

        if (result.success) {
          onPurchaseComplete({
            orderId: result.orderId,
            itemId: itemData.id,
            itemName: itemData.name,
            quantity: quantity,
            selectedSize: selectedSize,
            selectedColor: selectedColor,
            totalAmount: totalAmount,
            purchaseDate: new Date().toISOString(),
          });
        } else {
          alert(`Error: ${result.error}`);
        }
      }, 1000);
    } catch (error) {
      console.error("Error processing purchase:", error);
      setLoading(false);
      alert("Failed to process order. Please try again.");
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    alert("Copied to clipboard!");
  };

  // Apply glassomorphism styles similar to HomePage
  const glassStyles = {
    background: "rgba(255, 255, 255, 0.25)",
    backdropFilter: "blur(20px)",
    border: "1px solid rgba(255, 255, 255, 0.18)",
    borderRadius: "16px",
    boxShadow: "0 8px 32px rgba(11, 28, 72, 0.37)",
  };


  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: `linear-gradient(135deg, 
          ${primaryBlue[50]} 0%, 
          ${primaryBlue[100]} 25%, 
          ${primaryBlue[200]} 50%, 
          ${primaryBlue[100]} 75%, 
          ${primaryBlue[50]} 100%)`,
        position: "relative",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            radial-gradient(circle at 20% 50%, rgba(0, 114, 206, 0.1) 0%, transparent 50%),
            radial-gradient(circle at 80% 20%, rgba(11, 28, 72, 0.1) 0%, transparent 50%),
            radial-gradient(circle at 40% 80%, rgba(0, 34, 102, 0.1) 0%, transparent 50%)
          `,
          zIndex: 0,
        },
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box sx={{ position: "relative", zIndex: 1, flexGrow: 1 }}>
        {/* Custom AppBar for Checkout */}
        <AppBar
          position="static"
          elevation={0}
          sx={{
            ...glassStyles,
            bgcolor: "rgba(255, 255, 255, 0.15)",
            backdropFilter: "blur(25px)",
            borderBottom: "1px solid rgba(255, 255, 255, 0.2)",
            borderRadius: 0,
          }}
        >
          <Toolbar sx={{ py: 1 }}>
            <IconButton
              onClick={onBack}
              sx={{
                mr: 2,
                bgcolor: "rgba(255, 255, 255, 0.3)",
                "&:hover": { bgcolor: "rgba(255, 255, 255, 0.5)" },
              }}
            >
              <ArrowBack />
            </IconButton>
            <Typography
              variant="h4"
              component="div"
              sx={{
                flexGrow: 1,
                fontWeight: "bold",
                background: `linear-gradient(45deg, ${primaryBlue[700]}, ${primaryBlue[500]})`,
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                textShadow: "0 2px 4px rgba(0,0,0,0.1)",
              }}
            >
              InfinityWhale
            </Typography>
            <Typography
              variant="h5"
              sx={{
                fontWeight: "600",
                color: primaryBlue[700],
                textShadow: "0 1px 2px rgba(0,0,0,0.05)",
              }}
            >
              Checkout
            </Typography>
          </Toolbar>
        </AppBar>

        {/* Main Content */}
        <Container maxWidth="lg" sx={{ mt: 4, mb: 8 }}>
          <Grid container spacing={4}>
            {/* Left Column - Item Details */}
            <Grid item xs={12} md={6}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  mb: 4,
                  ...glassStyles,
                  bgcolor: "rgba(255, 255, 255, 0.5)",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                  <ShoppingBag sx={{ mr: 1, color: primaryBlue[500] }} />
                  <Typography
                    variant="h5"
                    sx={{ fontWeight: 600, color: primaryBlue[700] }}
                  >
                    Product Details
                  </Typography>
                </Box>

                <Card
                  elevation={0}
                  sx={{
                    mb: 3,
                    ...glassStyles,
                    bgcolor: "rgba(255, 255, 255, 0.7)",
                    overflow: "hidden",
                    border: `1px solid ${primaryBlue[100]}`,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: { xs: "column", sm: "row" },
                    }}
                  >
                    <CardMedia
                      component="img"
                      sx={{
                        width: { xs: "100%", sm: 180 },
                        height: { xs: 200, sm: "auto" },
                        objectFit: "contain",
                        p: 2,
                        bgcolor: "white",
                      }}
                      image={itemData.imageUrl}
                      alt={itemData.name}
                    />
                    <CardContent sx={{ flex: 1 }}>
                      <Box
                        sx={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 1,
                          mb: 1,
                          alignItems: "center",
                          justifyContent: "space-between",
                        }}
                      >
                        <Typography
                          variant="h5"
                          component="div"
                          sx={{ fontWeight: "bold", color: primaryBlue[800] }}
                        >
                          {itemData.name}
                        </Typography>

                        {itemData.category && (
                          <Chip
                            label={itemData.category}
                            size="small"
                            sx={{
                              bgcolor: "rgba(0, 114, 206, 0.8)",
                              color: "white",
                              fontWeight: 600,
                            }}
                          />
                        )}
                      </Box>

                      {itemData.brand && itemData.brand !== "No Brand" && (
                        <Typography
                          variant="body2"
                          sx={{ mb: 1, color: primaryBlue[600] }}
                        >
                          Brand: <b>{itemData.brand}</b>
                        </Typography>
                      )}

                      {itemData.rating > 0 && (
                        <Box
                          sx={{ display: "flex", alignItems: "center", mb: 1 }}
                        >
                          <Rating
                            value={itemData.rating}
                            readOnly
                            precision={0.5}
                            size="small"
                            sx={{
                              color: "#D4AF37",
                              mr: 1,
                            }}
                          />
                          <Typography variant="body2" color="text.secondary">
                            {itemData.rating}.0/5
                          </Typography>
                        </Box>
                      )}

                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 2 }}
                      >
                        {itemData.description?.substring(0, 120)}
                        {itemData.description?.length > 120 ? "..." : ""}
                      </Typography>

                      {/* Selected Options */}
                      <Box sx={{ mb: 2 }}>
                        {selectedSize && (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              mb: 1,
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 600,
                                mr: 1,
                                color: primaryBlue[700],
                              }}
                            >
                              Size:
                            </Typography>
                            <Chip
                              label={selectedSize}
                              size="small"
                              sx={{
                                bgcolor: primaryBlue[50],
                                color: primaryBlue[700],
                                fontWeight: 500,
                                border: `1px solid ${primaryBlue[200]}`,
                              }}
                            />
                          </Box>
                        )}

                        {selectedColor && (
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 600,
                                mr: 1,
                                color: primaryBlue[700],
                              }}
                            >
                              Color:
                            </Typography>
                            <Chip
                              label={selectedColor}
                              size="small"
                              sx={{
                                bgcolor: primaryBlue[50],
                                color: primaryBlue[700],
                                fontWeight: 500,
                                border: `1px solid ${primaryBlue[200]}`,
                              }}
                            />
                          </Box>
                        )}
                      </Box>

                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "baseline",
                        }}
                      >
                        <Typography
                          variant="h5"
                          sx={{
                            fontWeight: "bold",
                            background: `linear-gradient(45deg, ${primaryBlue[700]}, ${primaryBlue[500]})`,
                            backgroundClip: "text",
                            WebkitBackgroundClip: "text",
                            WebkitTextFillColor: "transparent",
                          }}
                        >
                          {formatPrice(itemData.sellingPrice)}
                        </Typography>

                        {itemData.compareAtPrice &&
                          itemData.compareAtPrice > itemData.sellingPrice && (
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <Typography
                                variant="body2"
                                sx={{
                                  textDecoration: "line-through",
                                  color: "text.secondary",
                                  fontWeight: 500,
                                }}
                              >
                                MRP: {formatPrice(itemData.compareAtPrice)}
                              </Typography>

                              <Chip
                                label={`${Math.round(
                                  (1 -
                                    itemData.sellingPrice /
                                      itemData.compareAtPrice) *
                                    100
                                )}% OFF`}
                                size="small"
                                sx={{
                                  bgcolor: "rgba(76, 175, 80, 0.1)",
                                  color: "success.main",
                                  fontWeight: 600,
                                  borderRadius: 1,
                                }}
                              />
                            </Box>
                          )}
                      </Box>
                    </CardContent>
                  </Box>
                </Card>

                {/* Key Product Information */}
                <Box sx={{ mb: 3 }}>
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 600, mb: 1, color: primaryBlue[700] }}
                  >
                    Product Information
                  </Typography>

                  <Grid container spacing={2}>
                    {itemData.warranty && (
                      <Grid item xs={12} sm={6}>
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <VerifiedUser
                            fontSize="small"
                            sx={{ color: primaryBlue[500], mr: 1 }}
                          />
                          <Typography variant="body2">
                            <b>Warranty:</b> {itemData.warranty}
                          </Typography>
                        </Box>
                      </Grid>
                    )}

                    {itemData.material && (
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2">
                          <b>Material:</b> {itemData.material}
                        </Typography>
                      </Grid>
                    )}

                    {itemData.weight && (
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2">
                          <b>Weight:</b> {itemData.weight}
                        </Typography>
                      </Grid>
                    )}

                    {itemData.manufacturer && (
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2">
                          <b>Manufacturer:</b> {itemData.manufacturer}
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                </Box>
              </Paper>
            </Grid>

            {/* Right Column - Order Summary & Payment */}
            <Grid item xs={12} md={6}>
              {/* Order Summary */}
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  mb: 4,
                  ...glassStyles,
                  bgcolor: "rgba(255, 255, 255, 0.5)",
                }}
              >
                <Typography
                  variant="h5"
                  sx={{ fontWeight: 600, mb: 3, color: primaryBlue[700] }}
                >
                  Order Summary
                </Typography>

                <TableContainer
                  component={Paper}
                  variant="outlined"
                  sx={{
                    borderRadius: 2,
                    overflow: "hidden",
                    mb: 3,
                    border: `1px solid ${primaryBlue[100]}`,
                  }}
                >
                  <Table>
                    <TableBody>
                      {/* Total MRP Row - Show original value */}
                      {hasSavings && originalTotal > 0 && (
                        <TableRow sx={{ bgcolor: "rgba(255, 152, 0, 0.08)" }}>
                          <TableCell
                            sx={{
                              borderBottom: `1px solid ${primaryBlue[100]}`,
                              py: 1.5,
                            }}
                          >
                            <Typography
                              variant="body1"
                              sx={{ fontWeight: 600, color: "#e65100" }}
                            >
                              Total MRP
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{ color: "text.secondary" }}
                            >
                              Original price before discount
                            </Typography>
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{
                              borderBottom: `1px solid ${primaryBlue[100]}`,
                              py: 1.5,
                            }}
                          >
                            <Typography
                              variant="body1"
                              sx={{
                                fontWeight: 600,
                                color: "text.secondary",
                                textDecoration: "line-through",
                              }}
                            >
                              {formatPrice(originalTotal)}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}

                      <TableRow>
                        <TableCell
                          sx={{
                            borderBottom: `1px solid ${primaryBlue[100]}`,
                            py: 2,
                          }}
                        >
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            <Typography
                              variant="body1"
                              sx={{ fontWeight: 500 }}
                            >
                              {itemData.name}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                ml: 1,
                                color: primaryBlue[600],
                                bgcolor: primaryBlue[50],
                                px: 1,
                                py: 0.5,
                                borderRadius: 1,
                              }}
                            >
                              x{quantity}
                            </Typography>
                          </Box>

                          <Box sx={{ mt: 1 }}>
                            {selectedSize && (
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                Size: {selectedSize}
                              </Typography>
                            )}

                            {selectedColor && (
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                Color: {selectedColor}
                              </Typography>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            borderBottom: `1px solid ${primaryBlue[100]}`,
                            py: 2,
                          }}
                        >
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {formatPrice(itemData.sellingPrice * quantity)}
                          </Typography>
                        </TableCell>
                      </TableRow>

                      {/* Money Saved Row */}
                      {hasSavings && (
                        <TableRow>
                          <TableCell sx={{ py: 1, borderBottom: "none" }}>
                            <Typography
                              variant="body1"
                              sx={{
                                fontWeight: 500,
                                color: "#00BFFF",
                              }}
                            >
                              You Save
                            </Typography>
                            {originalPrice > 0 && (
                              <Typography
                                variant="caption"
                                sx={{
                                  color: "text.secondary",
                                  display: "block",
                                }}
                              >
                                MRP: {formatPrice(originalTotal)}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{
                              py: 1,
                              borderBottom: "none",
                            }}
                          >
                            <Typography
                              variant="body1"
                              sx={{
                                fontWeight: 600,
                                color: "#00BFFF",
                              }}
                            >
                              -{formatPrice(moneySaved)}
                            </Typography>
                            {originalPrice > 0 && (
                              <Typography
                                variant="caption"
                                sx={{
                                  color: "#00BFFF",
                                  display: "block",
                                  fontWeight: 500,
                                }}
                              >
                                (
                                {Math.round((moneySaved / originalTotal) * 100)}
                                % off)
                              </Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      )}

                      {/* Subtotal Row */}
                      <TableRow>
                        <TableCell sx={{ py: 1 }}>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            Subtotal
                          </Typography>
                        </TableCell>
                        <TableCell align="right" sx={{ py: 1 }}>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {formatPrice(subtotal)}
                          </Typography>
                        </TableCell>
                      </TableRow>

                      {/* GST Row */}
                      <TableRow>
                        <TableCell sx={{ py: 1 }}>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            GST ({gstPercentage}%)
                          </Typography>
                        </TableCell>
                        <TableCell align="right" sx={{ py: 1 }}>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {formatPrice(gstAmount)}
                          </Typography>
                        </TableCell>
                      </TableRow>

                      <TableRow>
                        <TableCell sx={{ py: 1 }}>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            Delivery Fee
                          </Typography>
                        </TableCell>
                        <TableCell align="right" sx={{ py: 1 }}>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {deliveryFee === 0 ? 'FREE' : formatPrice(deliveryFee)}
                          </Typography>
                        </TableCell>
                      </TableRow>

                      <TableRow sx={{ bgcolor: primaryBlue[50] }}>
                        <TableCell sx={{ py: 2 }}>
                          <Typography
                            variant="subtitle1"
                            sx={{ fontWeight: 700 }}
                          >
                            Total Amount
                          </Typography>
                        </TableCell>
                        <TableCell align="right" sx={{ py: 2 }}>
                          <Typography
                            variant="h6"
                            sx={{
                              fontWeight: 700,
                              color: primaryBlue[700],
                            }}
                          >
                            {formatPrice(totalAmount)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Delivery Information */}
                <Card
                  variant="outlined"
                  sx={{
                    mb: 3,
                    background: "rgba(0, 114, 206, 0.1)",
                    backdropFilter: "blur(15px)",
                    border: `2px solid ${primaryBlue[200]}`,
                    borderRadius: "20px",
                  }}
                >
                  <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                    <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                      <LocalShipping sx={{ color: primaryBlue[600], mr: 1 }} />
                      <Typography
                        variant="h6"
                        sx={{
                          color: primaryBlue[700],
                          fontWeight: 600,
                        }}
                      >
                        Delivery Information
                      </Typography>
                    </Box>

                    <Box sx={{ mb: 2 }}>
                      <Typography
                        variant="body1"
                        sx={{
                          color: primaryBlue[700],
                          fontWeight: 600,
                          mb: 0.5,
                        }}
                      >
                        📦 Delivery in 7–10 days
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ color: primaryBlue[600] }}
                      >
                        Your order will be delivered within 7 to 10 business
                        days from order confirmation
                      </Typography>
                    </Box>

                    {deliveryMessage.isFree ? (
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <CheckCircle
                          sx={{ color: "#00BFFF", mr: 1, fontSize: 20 }}
                        />
                        <Typography
                          variant="body1"
                          sx={{ color: "#00BFFF", fontWeight: 600 }}
                        >
                          {deliveryMessage.message}
                        </Typography>
                      </Box>
                    ) : (
                      <Box>
                        <Typography
                          variant="body2"
                          sx={{ color: primaryBlue[600], fontWeight: 500 }}
                        >
                          {deliveryMessage.message}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ color: primaryBlue[500] }}
                        >
                          {deliveryMessage.additionalMessage}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ color: primaryBlue[400], mt: 0.5 }}
                        >
                          Free shipping on orders above ₹2,999
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>

                {/* Address Selection */}
                <Box sx={{ mb: 3 }}>
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 600, mb: 2, color: primaryBlue[700] }}
                  >
                    <LocationOn sx={{ mr: 1, verticalAlign: "text-bottom" }} />
                    Delivery Address
                  </Typography>

                  {addressLoading ? (
                    <Box>
                      <Skeleton
                        variant="rectangular"
                        height={60}
                        sx={{ mb: 1 }}
                      />
                      <Skeleton
                        variant="rectangular"
                        height={60}
                        sx={{ mb: 1 }}
                      />
                      <Skeleton variant="rectangular" height={60} />
                    </Box>
                  ) : addresses.length === 0 ? (
                    <Paper
                      sx={{
                        p: 3,
                        textAlign: "center",
                        bgcolor: "rgba(255, 152, 0, 0.05)",
                        border: "1px solid rgba(255, 152, 0, 0.2)",
                      }}
                    >
                      <Typography
                        variant="body1"
                        color="text.secondary"
                        gutterBottom
                      >
                        No saved addresses found
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 2 }}
                      >
                        Add an address in your profile settings to save time
                        during checkout
                      </Typography>
                      <Button
                        variant="outlined"
                        startIcon={<Add />}
                        sx={{
                          color: primaryBlue[600],
                          borderColor: primaryBlue[500],
                        }}
                      >
                        Add Address in Profile
                      </Button>
                    </Paper>
                  ) : (
                    <FormControl component="fieldset" sx={{ width: "100%" }}>
                      <RadioGroup
                        value={selectedAddress || ""}
                        onChange={handleAddressChange}
                      >
                        {addresses.map((address) => (
                          <FormControlLabel
                            key={address.id}
                            value={address.id}
                            control={<Radio />}
                            sx={{ alignItems: "flex-start", mb: 1 }}
                            label={
                              <Paper
                                sx={{
                                  p: 2,
                                  width: "100%",
                                  ml: 1,
                                  border:
                                    selectedAddress === address.id
                                      ? `2px solid ${primaryBlue[500]}`
                                      : "1px solid #e0e0e0",
                                  transition: "all 0.2s ease",
                                }}
                              >
                                <Box
                                  display="flex"
                                  justifyContent="space-between"
                                  alignItems="flex-start"
                                >
                                  <Box sx={{ flexGrow: 1 }}>
                                    <Typography
                                      variant="subtitle1"
                                      fontWeight={600}
                                    >
                                      {address.fullName}
                                      {address.isPrimary && (
                                        <Chip
                                          label="Primary"
                                          size="small"
                                          color="primary"
                                          sx={{ ml: 1 }}
                                        />
                                      )}
                                      {address.label && (
                                        <Chip
                                          label={address.label}
                                          size="small"
                                          variant="outlined"
                                          sx={{ ml: 1 }}
                                        />
                                      )}
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                    >
                                      {address.addressLine1}
                                      {address.addressLine2 &&
                                        `, ${address.addressLine2}`}
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                    >
                                      {address.city}, {address.state}{" "}
                                      {address.postalCode}
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                    >
                                      Phone: {address.phoneNumber}
                                    </Typography>
                                  </Box>
                                </Box>
                              </Paper>
                            }
                          />
                        ))}
                      </RadioGroup>
                    </FormControl>
                  )}
                </Box>

                {/* Shipping Note */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    mb: 3,
                    p: 2,
                    borderRadius: 2,
                    bgcolor: primaryBlue[50],
                    border: `1px solid ${primaryBlue[100]}`,
                  }}
                >
                  <LocalShipping sx={{ color: primaryBlue[500], mr: 1.5 }} />
                  <Typography variant="body2" sx={{ color: primaryBlue[700] }}>
                    <b>Free shipping</b> on orders above ₹999
                    <br />
                    Expected delivery: 3-5 business days
                  </Typography>
                </Box>
              </Paper>

              {/* Payment Information */}
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  ...glassStyles,
                  bgcolor: "rgba(255, 255, 255, 0.5)",
                }}
              >
                <Typography
                  variant="h5"
                  sx={{ fontWeight: 600, mb: 3, color: primaryBlue[700] }}
                >
                  Payment Information
                </Typography>

                <Card
                  variant="outlined"
                  sx={{
                    mb: 3,
                    bgcolor: "rgba(76, 175, 80, 0.05)",
                    borderColor: "rgba(76, 175, 80, 0.3)",
                    borderRadius: 2,
                  }}
                >
                  <CardContent>
                    <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                      <UPIIcon sx={{ color: "success.main", mr: 1 }} />
                      <Typography
                        variant="h6"
                        sx={{ fontWeight: 600, color: "success.dark" }}
                      >
                        UPI Payment
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        bgcolor: "white",
                        p: 2,
                        borderRadius: 1,
                        mb: 2,
                        border: "1px solid rgba(76, 175, 80, 0.2)",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            UPI ID:
                          </Typography>
                          <Typography
                            variant="h6"
                            sx={{
                              fontFamily: "monospace",
                              fontWeight: 600,
                              color: primaryBlue[800],
                            }}
                          >
                            {upiId}
                          </Typography>
                        </Box>
                        <IconButton
                          onClick={() => copyToClipboard(upiId)}
                          color="primary"
                          sx={{
                            bgcolor: primaryBlue[50],
                            "&:hover": {
                              bgcolor: primaryBlue[100],
                            },
                          }}
                        >
                          <ContentCopy />
                        </IconButton>
                      </Box>
                    </Box>

                    {/* Bank Details Section */}
                    <Box
                      sx={{
                        bgcolor: "rgba(255, 255, 255, 0.95)",
                        p: { xs: 2, sm: 3 },
                        borderRadius: 2,
                        border: `2px solid ${primaryBlue[200]}`,
                        mb: 3,
                        overflow: "hidden",
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        sx={{
                          color: primaryBlue[700],
                          fontWeight: 600,
                          mb: 2,
                          textAlign: "center",
                        }}
                      >
                        Bank Details
                      </Typography>

                      <Box
                        sx={{
                          bgcolor: "rgba(0, 114, 206, 0.05)",
                          p: 2,
                          borderRadius: 1,
                          border: `1px solid ${primaryBlue[200]}`,
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ mb: 1, color: primaryBlue[600] }}
                        >
                          <strong>Account Name:</strong> {BANK_DETAILS.accountName}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ mb: 1, color: primaryBlue[600] }}
                        >
                          <strong>Account Number:</strong> 44565656778
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ mb: 1, color: primaryBlue[600] }}
                        >
                          <strong>IFSC:</strong> SBIN0063847
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ color: primaryBlue[600] }}
                        >
                          <strong>Branch:</strong> BSF STC Yelahanka
                        </Typography>
                      </Box>
                    </Box>

                    <Typography
                      variant="subtitle2"
                      sx={{
                        mb: 1.5,
                        fontWeight: 600,
                        color: "success.dark",
                      }}
                    >
                      Payment Instructions:
                    </Typography>

                    <Box
                      sx={{
                        bgcolor: "white",
                        p: 2,
                        borderRadius: 1,
                        border: "1px solid rgba(76, 175, 80, 0.2)",
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          mb: 1,
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        <Box
                          component="span"
                          sx={{
                            minWidth: 20,
                            height: 20,
                            borderRadius: "50%",
                            bgcolor: primaryBlue[100],
                            color: primaryBlue[700],
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontWeight: "bold",
                            fontSize: "0.75rem",
                            mr: 1.5,
                          }}
                        >
                          1
                        </Box>
                        Open any UPI app (PhonePe, Google Pay, Paytm, etc.)
                      </Typography>

                      <Typography
                        variant="body2"
                        sx={{
                          mb: 1,
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        <Box
                          component="span"
                          sx={{
                            minWidth: 20,
                            height: 20,
                            borderRadius: "50%",
                            bgcolor: primaryBlue[100],
                            color: primaryBlue[700],
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontWeight: "bold",
                            fontSize: "0.75rem",
                            mr: 1.5,
                          }}
                        >
                          2
                        </Box>
                        Send <b>{formatPrice(totalAmount)}</b> to <b>{upiId}</b>
                      </Typography>

                      <Typography
                        variant="body2"
                        sx={{
                          mb: 1,
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        <Box
                          component="span"
                          sx={{
                            minWidth: 20,
                            height: 20,
                            borderRadius: "50%",
                            bgcolor: primaryBlue[100],
                            color: primaryBlue[700],
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontWeight: "bold",
                            fontSize: "0.75rem",
                            mr: 1.5,
                          }}
                        >
                          3
                        </Box>
                        Use reference: "Item - {itemData.name}"
                      </Typography>

                      <Typography
                        variant="body2"
                        sx={{
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        <Box
                          component="span"
                          sx={{
                            minWidth: 20,
                            height: 20,
                            borderRadius: "50%",
                            bgcolor: primaryBlue[100],
                            color: primaryBlue[700],
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontWeight: "bold",
                            fontSize: "0.75rem",
                            mr: 1.5,
                          }}
                        >
                          4
                        </Box>
                        Take a screenshot of successful payment
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>

                {/* Important Notes */}
                <Alert
                  severity="info"
                  sx={{
                    mb: 3,
                    borderRadius: 2,
                    "& .MuiAlert-icon": {
                      alignItems: "center",
                    },
                  }}
                >
                  <Typography variant="body2">
                    <b>Important:</b> Complete payment via Cashfree. Your order
                    is confirmed automatically after a successful payment.
                    <br />
                  </Typography>
                </Alert>

                {/* Action Buttons */}
                <Box sx={{ display: "flex", gap: 2, justifyContent: "center" }}>
                  <Button
                    variant="outlined"
                    size="large"
                    onClick={onBack}
                    sx={{
                      minWidth: 180,
                      py: 1.5,
                      borderColor: primaryBlue[300],
                      color: primaryBlue[600],
                      fontWeight: 600,
                      bgcolor: "rgba(255, 255, 255, 0.7)",
                      backdropFilter: "blur(10px)",
                      borderRadius: "10px",
                      "&:hover": {
                        borderColor: primaryBlue[500],
                        bgcolor: "rgba(255, 255, 255, 0.9)",
                        transform: "translateY(-2px)",
                      },
                      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    }}
                  >
                    Back to Shopping
                  </Button>

                  <Button
                    variant="contained"
                    size="large"
                    onClick={handlePayment}
                    startIcon={<PaymentIcon />}
                    sx={{
                      minWidth: 250,
                      py: 1.5,
                      background: `linear-gradient(45deg, ${primaryBlue[600]}, ${primaryBlue[500]})`,
                      border: "1px solid rgba(255, 255, 255, 0.3)",
                      borderRadius: "10px",
                      fontWeight: "bold",
                      boxShadow: "0 4px 20px rgba(0, 114, 206, 0.3)",
                      "&:hover": {
                        background: `linear-gradient(45deg, ${primaryBlue[700]}, ${primaryBlue[600]})`,
                        transform: "translateY(-2px)",
                        boxShadow: "0 6px 25px rgba(0, 114, 206, 0.4)",
                      },
                      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    }}
                  >
                    I've Completed Payment
                  </Button>
                </Box>
              </Paper>
            </Grid>
          </Grid>

          {/* Payment Confirmation Dialog */}
          <Dialog
            open={paymentConfirmDialog}
            onClose={() => !loading && setPaymentConfirmDialog(false)}
            maxWidth="sm"
            fullWidth
            PaperProps={{
              sx: {
                ...glassStyles,
                bgcolor: "rgba(255, 255, 255, 0.95)",
                backdropFilter: "blur(30px)",
              },
            }}
          >
            <DialogTitle sx={{ p: 3 }}>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <CheckCircle color="success" sx={{ mr: 1.5 }} />
                <Typography variant="h5" sx={{ fontWeight: 600 }}>
                  Confirm Payment
                </Typography>
              </Box>
            </DialogTitle>

            <DialogContent sx={{ p: 3 }}>
              <Typography variant="body1" gutterBottom>
                Please confirm that you have successfully sent{" "}
                <b>{formatPrice(totalAmount)}</b> to the UPI ID: <b>{upiId}</b>
              </Typography>

              <Box
                sx={{
                  mt: 3,
                  p: 2,
                  borderRadius: 2,
                  bgcolor: primaryBlue[50],
                  border: `1px solid ${primaryBlue[100]}`,
                }}
              >
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                  Order Details:
                </Typography>

                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  • Item: {itemData.name} x {quantity}
                </Typography>

                {selectedSize && (
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    • Size: {selectedSize}
                  </Typography>
                )}

                {selectedColor && (
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    • Color: {selectedColor}
                  </Typography>
                )}

                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  • Total: {formatPrice(totalAmount)}
                </Typography>
              </Box>
            </DialogContent>

            <DialogActions sx={{ p: 3, gap: 1 }}>
              <Button
                onClick={() => setPaymentConfirmDialog(false)}
                disabled={loading}
                variant="outlined"
                sx={{
                  borderColor: primaryBlue[300],
                  color: primaryBlue[600],
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handlePaymentConfirm}
                variant="contained"
                disabled={loading}
                sx={{
                  background: `linear-gradient(45deg, ${primaryBlue[600]}, ${primaryBlue[500]})`,
                  fontWeight: 600,
                }}
              >
                {loading ? "Processing..." : "Yes, I've Paid"}
              </Button>
            </DialogActions>
          </Dialog>
        </Container>
      </Box>

      {/* Footer */}
      <Footer primaryBlue={primaryBlue} glassStyles={glassStyles} />
    </Box>
  );
};

export default ItemCheckoutPage;
